import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Tag,
  Descriptions,
  Typography,
  Button,
  Table,
  Badge,
  Spin,
  Empty,
  Divider,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Checkbox,
  message,
  Tooltip,
  Alert,
  Popconfirm,
} from "antd";
import {
  ArrowLeftOutlined,
  LinkOutlined,
  GiftOutlined,
  PrinterOutlined,
  EditOutlined,
  EnvironmentOutlined,
  SwapOutlined,
  UnorderedListOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { supabase } from "../../lib/supabase";
import {
  lookupBookingForContainerLink,
  searchBookingsLinkableToContainer,
  linkBookingToContainer,
  type LinkableBooking,
} from "../../services/bookingService";
import { ContainerWithInfo } from "../../pages/ContainersAdmin";
import {
  getContainerEmailsDetailed,
  ContainerEmailData,
} from "../../services/containerService";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/es/table";
import {
  statusColors,
  normalizeBookingStatus,
} from "../../utils/bookingStatus";
import { VesselFinderMap } from "../VesselFinderMap";
import ContainerDocuments from "./ContainerDocuments";
import ContainerDocumentsSummary from "./ContainerDocumentsSummary";
import ContainerBlNumberDisplay from "./ContainerBlNumberDisplay";
import { ContainerMasterAwbBlReadOnly } from "./MasterContainerAwbBlSection";
const { Title, Text } = Typography;
const { Option } = Select;

interface Booking {
  id: number;
  reference: string;
  status: string;
  created_at: string;
  eta: string | null;
  container_id: number | null;
  company_info?: {
    id: number;
    name: string;
  };
  supplier_info?: {
    id: number;
    name: string;
  };
  transport_type_info?: {
    id: number;
    name: string;
    abbreviation: string;
  };
  booking_type_info?: {
    id: number;
    name: string;
  };
  booking_delivery_info?:
    | {
        id: number;
        street_name: string;
        house_number: string;
        city: string;
        postal_code: string;
        country: string;
      }
    | {
        id: number;
        street_name: string;
        house_number: string;
        city: string;
        postal_code: string;
        country: string;
      }[];
  total_cartons?: number;
  total_cbm?: number;
  total_weight?: number;
  export_clearance?: boolean | null;
  pick_up_fee?: number | null;
  commodity_inspection?: boolean | null;
  post_export_license?: number | null;
}

interface Product {
  id: number;
  booking_id: number;
  booking_reference?: string;
  booking_transport_type?: string;
  company_name?: string;
  name: string;
  ean: string | number | null;
  hs_code: string | null;
  pieces: number | null;
  cartons: number | null;
  gross_weight: number | null;
  cbm: number | null;
  value: number | null;
  booking_delivery_info?:
    | {
        id: number;
        street_name: string;
        house_number: string;
        city: string;
        postal_code: string;
        country: string;
      }
    | {
        id: number;
        street_name: string;
        house_number: string;
        city: string;
        postal_code: string;
        country: string;
      }[];
}

interface ContainerDetailsProps {
  containerId: number;
  onBack: () => void;
}

const ContainerDetails: React.FC<ContainerDetailsProps> = ({
  containerId,
  onBack,
}) => {
  const [container, setContainer] = useState<ContainerWithInfo | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [transportTypes, setTransportTypes] = useState<
    { id: number; name: string }[]
  >([]);
  const [originPorts, setOriginPorts] = useState<
    { id: number; name: string; country: string }[]
  >([]);
  const [destinationPorts, setDestinationPorts] = useState<
    { id: number; name: string; country: string }[]
  >([]);

  // Add/remove booking state
  const [isAddBookingModalVisible, setIsAddBookingModalVisible] =
    useState(false);
  const [availableBookings, setAvailableBookings] = useState<LinkableBooking[]>(
    []
  );
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(
    null
  );
  const [isLoadingAvailableBookings, setIsLoadingAvailableBookings] =
    useState(false);
  const linkableBookingsSearchDebounceRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  // Status update state
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);

  // Master container state
  const [masterContainer, setMasterContainer] = useState<{
    id: number;
    reference: string | null;
    containers?: Array<{
      id: number;
      reference: string | null;
      status: string | null;
    }>;
  } | null>(null);
  const [masterContainerLoading, setMasterContainerLoading] = useState(false);

  // Email modal state
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [emailData, setEmailData] = useState<ContainerEmailData[]>([]);
  const [emailsLoading, setEmailsLoading] = useState(false);

  // Print filter state
  const [printFilter, setPrintFilter] = useState<"all" | "storage" | "delivery">("all");

  // Single product filter state
  const [filterOutSingleProducts, setFilterOutSingleProducts] = useState(false);

  // Fetch container details
  useEffect(() => {
    const fetchContainer = async () => {
      try {
        setLoading(true);

        const { data, error } = await supabase
          .from("containers")
          .select(
            `
            *,
            transport_type_info:booking_transport_types(*),
            origin_port_info:booking_origin_port(*),
            destination_port_info:booking_destination_port(*)
          `
          )
          .eq("id", containerId)
          .single();

        if (error) throw error;
        setContainer(data);
      } catch (error) {
        console.error("Error fetching container:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchContainer();
  }, [containerId]);

  // Fetch master container information
  useEffect(() => {
    const fetchMasterContainer = async () => {
      try {
        setMasterContainerLoading(true);

        // Check if this container is part of a master container
        const { data: masterData, error: masterError } = await supabase
          .from("master_containers_containers")
          .select(
            `
            master_container:master_containers(
              id,
              reference,
              containers:master_containers_containers(
                container:containers(id, reference, status)
              )
            )
          `
          )
          .eq("container_id", containerId)
          .single();

        if (masterError && masterError.code !== "PGRST116") {
          throw masterError;
        }

        if (masterData && masterData.master_container) {
          const masterContainerData = Array.isArray(masterData.master_container)
            ? masterData.master_container[0]
            : masterData.master_container;

          // Format the containers data
          const containers =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            masterContainerData.containers?.map((item: any) =>
              Array.isArray(item.container) ? item.container[0] : item.container
            ) || [];

          setMasterContainer({
            ...masterContainerData,
            containers,
          });
        }
      } catch (error) {
        console.error("Error fetching master container:", error);
      } finally {
        setMasterContainerLoading(false);
      }
    };

    fetchMasterContainer();
  }, [containerId]);

  // Define a fetchBookings function to be called from multiple places
  const fetchBookings = async () => {
    try {
      setBookingsLoading(true);

      // First, fetch the basic booking information
      const { data: bookingsData, error: bookingsError } = await supabase
        .from("bookings")
        .select(
          `
          id,
          reference,
          status,
          created_at,
          eta,
          container_id,
          export_clearance,
          pick_up_fee,
          commodity_inspection,
          post_export_license,
          company_info:company_id(id, name),
          supplier_info:supplier_id(id, name),
          transport_type_info:transport_type(id, name, abbreviation),
          booking_type_info:booking_type(id, name),
          booking_delivery_info:booking_delivery(id, street_name, house_number, city, postal_code, country)
        `
        )
        .eq("container_id", containerId);

      if (bookingsError) throw bookingsError;

      if (bookingsData && bookingsData.length > 0) {
        // For each booking, fetch its products to calculate totals
        const bookingsWithTotals = await Promise.all(
          bookingsData.map(async (booking) => {
            // Get products for this booking
            const { data: productData, error: productError } = await supabase
              .from("booking_product") // Using the correct table name
              .select("cartons, cbm, gross_weight")
              .eq("booking_id", booking.id);

            if (productError) {
              console.error(
                `Error fetching products for booking ${booking.id}:`,
                productError
              );
              return {
                ...booking,
                company_info: Array.isArray(booking.company_info)
                  ? booking.company_info[0]
                  : booking.company_info,
                supplier_info: Array.isArray(booking.supplier_info)
                  ? booking.supplier_info[0]
                  : booking.supplier_info,
                transport_type_info: Array.isArray(booking.transport_type_info)
                  ? booking.transport_type_info[0]
                  : booking.transport_type_info,
                booking_type_info: Array.isArray(booking.booking_type_info)
                  ? booking.booking_type_info[0]
                  : booking.booking_type_info,
                total_cartons: 0,
                total_cbm: 0,
                total_weight: 0,
              };
            }

            // Calculate totals from product data
            const totalCartons =
              productData?.reduce(
                (sum, product) => sum + (product.cartons || 0),
                0
              ) || 0;

            const totalCbm =
              productData?.reduce(
                (sum, product) => sum + (product.cbm || 0),
                0
              ) || 0;

            const totalWeight =
              productData?.reduce(
                (sum, product) => sum + (product.gross_weight || 0),
                0
              ) || 0;

            return {
              ...booking,
              company_info: Array.isArray(booking.company_info)
                ? booking.company_info[0]
                : booking.company_info,
              supplier_info: Array.isArray(booking.supplier_info)
                ? booking.supplier_info[0]
                : booking.supplier_info,
              transport_type_info: Array.isArray(booking.transport_type_info)
                ? booking.transport_type_info[0]
                : booking.transport_type_info,
              booking_type_info: Array.isArray(booking.booking_type_info)
                ? booking.booking_type_info[0]
                : booking.booking_type_info,
              total_cartons: totalCartons,
              total_cbm: totalCbm,
              total_weight: totalWeight,
            };
          })
        );

        console.log("Fetched bookings with totals:", bookingsWithTotals);
        setBookings(bookingsWithTotals);

        // After we have the bookings, fetch all products for the container
        fetchProducts(bookingsWithTotals);
      } else {
        setBookings([]);
        setProductsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setBookings([]);
      setProductsLoading(false);
    } finally {
      setBookingsLoading(false);
    }
  };

  const fetchProducts = async (bookings: Booking[]) => {
    try {
      setProductsLoading(true);

      // Create maps for booking reference, transport type, company name, and delivery info lookup
      const bookingRefMap = new Map<number, string>();
      const bookingTransportMap = new Map<number, string>();
      const bookingCompanyMap = new Map<number, string>();
      const bookingDeliveryMap = new Map<
        number,
        Booking["booking_delivery_info"]
      >();

      bookings.forEach((booking) => {
        bookingRefMap.set(booking.id, booking.reference);
        bookingTransportMap.set(
          booking.id,
          booking.transport_type_info?.abbreviation || ""
        );
        bookingCompanyMap.set(booking.id, booking.company_info?.name || "-");
        bookingDeliveryMap.set(booking.id, booking.booking_delivery_info);
      });

      // Build an array of promises for each booking's products
      const promises = bookings.map(async (booking) => {
        const { data, error } = await supabase
          .from("booking_product") // Correct table name
          .select(
            `
            id,
            booking_id,
            name,
            ean,
            hs_code,
            pieces,
            cartons,
            gross_weight,
            cbm,
            value
          `
          )
          .eq("booking_id", booking.id);

        if (error) throw error;

        // Add booking reference and delivery info to each product
        return (
          data?.map(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (product: Record<string, any>) =>
              ({
                id: product.id,
                booking_id: product.booking_id,
                name: product.name,
                ean: product.ean,
                hs_code: product.hs_code,
                pieces: product.pieces,
                cartons: product.cartons,
                gross_weight: product.gross_weight,
                cbm: product.cbm,
                value: product.value,
                booking_reference: bookingRefMap.get(product.booking_id) || `-`,
                booking_transport_type:
                  bookingTransportMap.get(product.booking_id) || `-`,
                company_name: bookingCompanyMap.get(product.booking_id) || `-`,
                booking_delivery_info: bookingDeliveryMap.get(
                  product.booking_id
                ),
              } as Product)
          ) || []
        );
      });

      // Combine all products from all bookings
      const results = await Promise.all(promises);
      const allProducts = results.flat();

      console.log("Fetched products:", allProducts);
      setProducts(allProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  // Fetch container bookings
  useEffect(() => {
    if (!loading && container) {
      fetchBookings();
    }
  }, [containerId, loading, container]);

  // Fetch reference data for the edit form
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        // Fetch transport types
        const { data: transportData, error: transportError } = await supabase
          .from("booking_transport_types")
          .select("id, name, abbreviation");

        if (transportError) throw transportError;
        setTransportTypes(transportData || []);

        // Fetch origin ports
        const { data: originData, error: originError } = await supabase
          .from("booking_origin_port")
          .select("id, name, country");

        if (originError) throw originError;
        setOriginPorts(originData || []);

        // Fetch destination ports
        const { data: destData, error: destError } = await supabase
          .from("booking_destination_port")
          .select("id, name, country");

        if (destError) throw destError;
        setDestinationPorts(destData || []);
      } catch (error) {
        console.error("Error fetching reference data:", error);
        message.error("Failed to load reference data");
      }
    };

    fetchReferenceData();
  }, []);

  const getContainerStatusColor = (status: string) => {
    const statusMap: Record<string, string> = {
      planned: "default",
      departed: "processing",
      arrived: "success",
      customs: "warning",
      delivered: "success",
      completed: "success",
      cancelled: "error",
    };
    return statusMap[status?.toLowerCase()] || "default";
  };

  const bookingsColumns: ColumnsType<Booking> = [
    {
      title: "Reference",
      dataIndex: "id",
      key: "reference",
      render: (id, record) => {
        const transportType = record.transport_type_info?.abbreviation || "";
        return (
          <Tag color="blue">
            EFF{id}
            {transportType}
          </Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const normalizedStatus = normalizeBookingStatus(status);
        const statusColor =
          statusColors[normalizedStatus as keyof typeof statusColors] ||
          "default";
        return <Tag color={statusColor}>{normalizedStatus}</Tag>;
      },
    },
    {
      title: "Transport Type",
      key: "transport_type",
      dataIndex: ["transport_type_info"],
      render: (transportInfo) => {
        if (!transportInfo) return "-";
        const typeName = Array.isArray(transportInfo)
          ? transportInfo[0]?.name || "-"
          : transportInfo.name || "-";
        return typeName;
      },
    },
    {
      title: "Booking Type",
      key: "booking_type",
      dataIndex: ["booking_type_info"],
      render: (bookingTypeInfo) => {
        if (!bookingTypeInfo) return "-";
        const typeName = Array.isArray(bookingTypeInfo)
          ? bookingTypeInfo[0]?.name || "-"
          : bookingTypeInfo.name || "-";
        return typeName;
      },
    },
    {
      title: "Company",
      dataIndex: ["company_info", "name"],
      key: "company",
      render: (text) => text || "-",
    },
    {
      title: "Supplier",
      dataIndex: ["supplier_info", "name"],
      key: "supplier",
      render: (text) => text || "-",
    },
    {
      title: "Cartons",
      dataIndex: "total_cartons",
      key: "total_cartons",
      render: (cartons) => (cartons > 0 ? cartons : "-"),
    },
    {
      title: "Gross Weight",
      dataIndex: "total_weight",
      key: "total_weight",
      render: (weight) =>
        weight > 0 ? `${Number(weight).toFixed(2)} kg` : "-",
    },
    {
      title: "CBM",
      dataIndex: "total_cbm",
      key: "total_cbm",
      render: (cbm) => (cbm > 0 ? `${Number(cbm).toFixed(3)} m³` : "-"),
    },
    {
      title: "Delivery",
      key: "delivery",
      width: 100,
      render: (_, record) => {
        // Get delivery info
        const deliveryInfo = record.booking_delivery_info;

        // Check if delivery info exists and has valid data
        const hasDelivery =
          deliveryInfo &&
          ((Array.isArray(deliveryInfo) && deliveryInfo.length > 0) ||
            (typeof deliveryInfo === "object" &&
              !Array.isArray(deliveryInfo) &&
              deliveryInfo !== null));

        // If no delivery record exists, show storage
        if (!hasDelivery) {
          return (
            <Tooltip title="In opslag">
              <Tag color="orange">In opslag</Tag>
            </Tooltip>
          );
        }

        return (
          <Tooltip title="Uitlevering">
            <Tag color="blue">Uitlevering</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Export license",
      dataIndex: "export_clearance",
      key: "export_clearance",
      render: (v: boolean | null) => (v ? "Y" : "-"),
    },
    {
      title: "Commodity inspection",
      dataIndex: "commodity_inspection",
      key: "commodity_inspection",
      render: (v: boolean | null) => (v ? "Y" : "-"),
    },
    {
      title: "Post export license",
      dataIndex: "post_export_license",
      key: "post_export_license",
      render: (v: number | null) => (v ? `€${v.toFixed(0)}` : "-"),
    },
    {
      title: "Pickup fee",
      dataIndex: "pick_up_fee",
      key: "pick_up_fee",
      render: (v: number | null) => (v ? `€${v.toFixed(0)}` : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Popconfirm
          title="Remove this booking from container?"
          onConfirm={(e) => removeBookingFromContainer(record.id, e)}
          okText="Yes"
          cancelText="No"
          onCancel={(e) => {
            if (e) e.stopPropagation();
          }}
        >
          <Button
            type="link"
            danger
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const productsColumns: ColumnsType<Product> = [
    {
      title: "Booking Ref",
      dataIndex: "booking_id",
      key: "booking_id",
      render: (id, record) => {
        const transportType = record.booking_transport_type || "";
        return (
          <Tag
            color="blue"
            style={{ cursor: "pointer" }}
            onClick={(e) => {
              e.stopPropagation();
              navigateToBookingDetails(id);
            }}
          >
            EFF{id}
            {transportType}
          </Tag>
        );
      },
    },
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
    },
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "EAN",
      dataIndex: "ean",
      key: "ean",
      render: (ean) => ean || "-",
    },
    {
      title: "HS Code",
      dataIndex: "hs_code",
      key: "hs_code",
      render: (hsCode) => hsCode || "-",
    },
    {
      title: "Pieces",
      dataIndex: "pieces",
      key: "pieces",
      render: (pieces) => pieces || "-",
    },
    {
      title: "Cartons",
      dataIndex: "cartons",
      key: "cartons",
      render: (cartons) => cartons || "-",
    },
    {
      title: "Gross Weight (kg)",
      dataIndex: "gross_weight",
      key: "gross_weight",
      render: (weight) => (weight ? `${weight} kg` : "-"),
    },
    {
      title: "CBM",
      dataIndex: "cbm",
      key: "cbm",
      render: (cbm) => (cbm ? `${Number(cbm).toFixed(3)} m³` : "-"),
    },
    {
      title: "Delivery",
      key: "delivery",
      width: 100,
      render: (_, record) => {
        // Get delivery info from the booking
        const deliveryInfo = record.booking_delivery_info;

        // Check if delivery info exists and has valid data
        const hasDelivery =
          deliveryInfo &&
          ((Array.isArray(deliveryInfo) && deliveryInfo.length > 0) ||
            (typeof deliveryInfo === "object" &&
              !Array.isArray(deliveryInfo) &&
              deliveryInfo !== null));

        // If no delivery record exists, show storage
        if (!hasDelivery) {
          return (
            <Tooltip title="In opslag">
              <Tag color="orange">In opslag</Tag>
            </Tooltip>
          );
        }

        return (
          <Tooltip title="Uitlevering">
            <Tag color="blue">Uitlevering</Tag>
          </Tooltip>
        );
      },
    },
  ];

  // Create a map of booking IDs to their product count
  const bookingProductCount = new Map<number, number>();
  products.forEach((product) => {
    const count = bookingProductCount.get(product.booking_id) || 0;
    bookingProductCount.set(product.booking_id, count + 1);
  });

  // Filter products based on selected filter
  const filteredProducts = products.filter((product) => {
    const deliveryInfo = product.booking_delivery_info;
    const hasDelivery =
      deliveryInfo &&
      ((Array.isArray(deliveryInfo) && deliveryInfo.length > 0) ||
        (typeof deliveryInfo === "object" &&
          !Array.isArray(deliveryInfo) &&
          deliveryInfo !== null));

    // First, filter by delivery/storage type
    let passesDeliveryFilter = false;
    switch (printFilter) {
      case "storage":
        passesDeliveryFilter = !hasDelivery;
        break;
      case "delivery":
        passesDeliveryFilter = hasDelivery;
        break;
      case "all":
      default:
        passesDeliveryFilter = true;
        break;
    }

    // Then, filter out single products if checkbox is checked
    const productCount = bookingProductCount.get(product.booking_id) || 0;
    const passesSingleProductFilter = !filterOutSingleProducts || productCount > 1;

    return passesDeliveryFilter && passesSingleProductFilter;
  });

  // Calculate totals for products
  const totalPieces = products.reduce(
    (sum, product) => sum + (product.pieces || 0),
    0
  );
  const totalCartons = products.reduce(
    (sum, product) => sum + (product.cartons || 0),
    0
  );
  const totalWeight = products.reduce(
    (sum, product) => sum + (product.gross_weight || 0),
    0
  );
  const totalCbm = products
    .reduce((sum, product) => sum + (product.cbm || 0), 0)
    .toFixed(3);

  // Calculate totals for bookings
  const bookingsTotalCartons = bookings.reduce(
    (sum, booking) => sum + (booking.total_cartons || 0),
    0
  );
  const bookingsTotalWeight = bookings.reduce(
    (sum, booking) => sum + (booking.total_weight || 0),
    0
  );
  const bookingsTotalCbm = bookings
    .reduce((sum, booking) => sum + (booking.total_cbm || 0), 0)
    .toFixed(3);

  // Generate printable document for bookings
  const generateBookingsPrintContent = () => {
    const date = new Date().toLocaleDateString();
    const title = `Container ${container?.reference || containerId} Bookings`;

    let content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <style>
          @page {
            size: A4 landscape;
            margin: 1.5cm;
          }
          body {
            font-family: Arial, sans-serif;
            line-height: 1.3;
            font-size: 10px;
            color: #000;
          }
          h1 {
            font-size: 16px;
            text-align: center;
            margin-bottom: 15px;
          }
          .container-info {
            margin-bottom: 15px;
            border-bottom: 1px solid #ddd;
            padding-bottom: 8px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 6px;
            text-align: left;
            font-size: 9px;
          }
          th {
            background-color: #f2f2f2;
            font-weight: bold;
          }
          .footer {
            margin-top: 15px;
            text-align: center;
            font-size: 9px;
            color: #666;
          }
          .tag {
            display: inline-block;
            padding: 2px 4px;
            border-radius: 3px;
            font-size: 8px;
          }
          .tag-default { background-color: #d9d9d9; }
          .tag-processing { background-color: #1890ff; color: white; }
          .tag-success { background-color: #52c41a; color: white; }
          .tag-warning { background-color: #faad14; }
          .tag-error { background-color: #ff4d4f; color: white; }
          .ref-tag {
            background-color: #1890ff;
            color: white;
            padding: 2px 4px;
            border-radius: 3px;
            font-size: 8px;
          }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        
        <div class="container-info">
          <p><strong>Container Reference:</strong> ${
            container?.reference || "-"
          }</p>
          <p><strong>Transport Type:</strong> ${
            container?.transport_type_info?.name || "-"
          }</p>
          <p><strong>Status:</strong> ${container?.status || "-"}</p>
          <p><strong>Origin:</strong> ${
            container?.origin_port_info?.name || "-"
          } (${container?.origin_port_info?.country || "-"})</p>
          <p><strong>Destination:</strong> ${
            container?.destination_port_info?.name || "-"
          } (${container?.destination_port_info?.country || "-"})</p>
          <p><strong>ETD:</strong> ${
            container?.delayed_etd
              ? dayjs(container.delayed_etd).format("DD/MM/YYYY")
              : container?.etd
                ? dayjs(container.etd).format("DD/MM/YYYY")
                : "-"
          }</p>
          ${
            container?.delayed_etd && container?.etd
              ? `<p><strong>Original ETD:</strong> ${dayjs(container.etd).format("DD/MM/YYYY")}</p>`
              : ""
          }
          <p><strong>ETA:</strong> ${
            container?.eta ? dayjs(container.eta).format("DD/MM/YYYY") : "-"
          }</p>
          <p><strong>Customer ETA:</strong> ${
            container?.eta_customer
              ? dayjs(container.eta_customer).format("DD/MM/YYYY")
              : "-"
          }</p>
          <p><strong>Print Date:</strong> ${date}</p>
        </div>
        
        <h2>Bookings (${bookings.length})</h2>
        <table>
          <thead>
            <tr>
              <th>Reference</th>
              <th>Company</th>
              <th>Pallets</th>
              <th>Cartons</th>
              <th>Gross Weight</th>
              <th>CBM</th>
              <th>Delivery</th>
            </tr>
          </thead>
          <tbody>
    `;

    if (bookings.length === 0) {
      content += `
            <tr>
              <td colspan="7" style="text-align: center">No bookings in this container</td>
            </tr>
      `;
    } else {
      bookings.forEach((booking) => {
        const transportType = booking.transport_type_info?.abbreviation || "";
        const status = normalizeBookingStatus(booking.status);
        const statusColor =
          statusColors[status as keyof typeof statusColors] || "default";

        // Get delivery info for print
        const deliveryInfo = booking.booking_delivery_info;
        const hasDelivery =
          deliveryInfo &&
          ((Array.isArray(deliveryInfo) && deliveryInfo.length > 0) ||
            (typeof deliveryInfo === "object" &&
              !Array.isArray(deliveryInfo) &&
              deliveryInfo !== null));
        const deliveryStatus = hasDelivery ? "Uitlevering" : "In opslag";

        content += `
            <tr>
              <td>EFF${booking.id}${transportType}</td>
              <td>${booking.company_info?.name || "-"}</td>
              <td></td>
              <td>${
                (booking.total_cartons || 0) > 0 ? booking.total_cartons : "-"
              }</td>
              <td>${
                (booking.total_weight || 0) > 0
                  ? `${Number(booking.total_weight).toFixed(2)} kg`
                  : "-"
              }</td>
              <td>${
                (booking.total_cbm || 0) > 0
                  ? `${Number(booking.total_cbm).toFixed(3)} m³`
                  : "-"
              }</td>
              <td>${deliveryStatus}</td>
            </tr>
        `;
      });
    }

    content += `
          </tbody>
          <tfoot>
            <tr>
              <td colspan="2" style="text-align: right;"><strong>Totals:</strong></td>
              <td><strong>-</strong></td>
              <td><strong>${bookingsTotalCartons}</strong></td>
              <td><strong>${bookingsTotalWeight.toFixed(2)} kg</strong></td>
              <td><strong>${bookingsTotalCbm} m³</strong></td>
              <td><strong>-</strong></td>
            </tr>
          </tfoot>
        </table>
        
        <div class="footer">
          <p>Document generated on ${date}</p>
        </div>
      </body>
      </html>
    `;

    return content;
  };

  // Generate printable document for products
  const generateProductsPrintContent = () => {
    const date = new Date().toLocaleDateString();

    // Calculate totals for filtered products (reuse the filteredProducts from above)
    const filteredTotalPieces = filteredProducts.reduce(
      (sum, product) => sum + (product.pieces || 0),
      0
    );
    const filteredTotalCartons = filteredProducts.reduce(
      (sum, product) => sum + (product.cartons || 0),
      0
    );
    const filteredTotalWeight = filteredProducts.reduce(
      (sum, product) => sum + (product.gross_weight || 0),
      0
    );
    const filteredTotalCbm = filteredProducts
      .reduce((sum, product) => sum + (product.cbm || 0), 0)
      .toFixed(3);

    let filterText = printFilter === "all" ? "" : ` (${printFilter === "storage" ? "In Storage" : "Delivery"})`;
    if (filterOutSingleProducts) {
      filterText += filterText ? " - Single Products Filtered Out" : " (Single Products Filtered Out)";
    }
    const title = `Container ${container?.reference || containerId} Products${filterText}`;

    let content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <style>
          @page {
            size: A4 landscape;
            margin: 1.5cm;
          }
          body {
            font-family: Arial, sans-serif;
            line-height: 1.3;
            font-size: 10px;
            color: #000;
          }
          h1 {
            font-size: 16px;
            text-align: center;
            margin-bottom: 15px;
          }
          .container-info {
            margin-bottom: 15px;
            border-bottom: 1px solid #ddd;
            padding-bottom: 8px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 5px;
            text-align: left;
            font-size: 8px;
          }
          th {
            background-color: #f2f2f2;
            font-weight: bold;
          }
          .footer {
            margin-top: 15px;
            text-align: center;
            font-size: 9px;
            color: #666;
          }
          .ref-tag {
            display: inline-block;
            padding: 2px 4px;
            border-radius: 3px;
            font-size: 8px;
            background-color: #1890ff;
            color: white;
          }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        
        <div class="container-info">
          <p><strong>Container Reference:</strong> ${
            container?.reference || "-"
          }</p>
          <p><strong>Transport Type:</strong> ${
            container?.transport_type_info?.name || "-"
          }</p>
          <p><strong>Status:</strong> ${container?.status || "-"}</p>
          <p><strong>Origin:</strong> ${
            container?.origin_port_info?.name || "-"
          } (${container?.origin_port_info?.country || "-"})</p>
          <p><strong>Destination:</strong> ${
            container?.destination_port_info?.name || "-"
          } (${container?.destination_port_info?.country || "-"})</p>
          <p><strong>ETD:</strong> ${
            container?.delayed_etd
              ? dayjs(container.delayed_etd).format("DD/MM/YYYY")
              : container?.etd
                ? dayjs(container.etd).format("DD/MM/YYYY")
                : "-"
          }</p>
          ${
            container?.delayed_etd && container?.etd
              ? `<p><strong>Original ETD:</strong> ${dayjs(container.etd).format("DD/MM/YYYY")}</p>`
              : ""
          }
          <p><strong>ETA:</strong> ${
            container?.eta ? dayjs(container.eta).format("DD/MM/YYYY") : "-"
          }</p>
          <p><strong>Customer ETA:</strong> ${
            container?.eta_customer
              ? dayjs(container.eta_customer).format("DD/MM/YYYY")
              : "-"
          }</p>
          <p><strong>Print Date:</strong> ${date}</p>
        </div>
        
        <h2>Products (${filteredProducts.length})</h2>
        <table>
          <thead>
            <tr>
              <th>Booking Ref</th>
              <th>Company</th>
              <th>EAN</th>
              <th>Pieces</th>
              <th>Cartons</th>
              <th>CBM</th>
              <th>Delivery</th>
            </tr>
          </thead>
          <tbody>
    `;

    if (filteredProducts.length === 0) {
      content += `
            <tr>
              <td colspan="7" style="text-align: center">No products match the selected filter</td>
            </tr>
      `;
    } else {
      filteredProducts.forEach((product) => {
        // Get delivery info for print
        const deliveryInfo = product.booking_delivery_info;
        const hasDelivery =
          deliveryInfo &&
          ((Array.isArray(deliveryInfo) && deliveryInfo.length > 0) ||
            (typeof deliveryInfo === "object" &&
              !Array.isArray(deliveryInfo) &&
              deliveryInfo !== null));
        const deliveryStatus = hasDelivery ? "Uitlevering" : "In opslag";

        content += `
            <tr>
              <td>EFF${product.booking_id}${product.booking_transport_type || ""}</td>
              <td>${product.company_name || "-"}</td>
              <td>${product.ean || "-"}</td>
              <td>${product.pieces || "-"}</td>
              <td>${product.cartons || "-"}</td>
              <td>${
                product.cbm ? `${Number(product.cbm).toFixed(3)} m³` : "-"
              }</td>
              <td>${deliveryStatus}</td>
            </tr>
        `;
      });
    }

    content += `
          </tbody>
          <tfoot>
            <tr>
              <td colspan="3" style="text-align: right;"><strong>Totals:</strong></td>
              <td><strong>${filteredTotalPieces}</strong></td>
              <td><strong>${filteredTotalCartons}</strong></td>
              <td><strong>${filteredTotalCbm} m³</strong></td>
              <td><strong>-</strong></td>
            </tr>
          </tfoot>
        </table>
        
        <div class="footer">
          <p>Document generated on ${date}</p>
        </div>
      </body>
      </html>
    `;

    return content;
  };

  // Open a new window with the printable content
  const openPrintWindow = (content: string) => {
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(content);
      printWindow.document.close();
      printWindow.focus();
      // Auto print
      setTimeout(() => {
        printWindow.print();
      }, 500);
    } else {
      alert("Please allow popups to print this document");
    }
  };

  // Handle print buttons
  const handlePrintBookings = () => {
    const content = generateBookingsPrintContent();
    openPrintWindow(content);
  };

  const handlePrintProducts = () => {
    const content = generateProductsPrintContent();
    openPrintWindow(content);
  };

  const showEditModal = () => {
    if (!container) return;

    form.setFieldsValue({
      reference: container.reference,
      transport_type: container.transport_type,
      bl_number: container.bl_number,
      etd: container.etd ? dayjs(container.etd) : null,
      delayed_etd: container.delayed_etd ? dayjs(container.delayed_etd) : null,
      eta: container.eta ? dayjs(container.eta) : null,
      eta_customer: container.eta_customer
        ? dayjs(container.eta_customer)
        : null,
      track_and_trace: container.track_and_trace,
      origin_port: container.origin_port,
      destination_port: container.destination_port,
    });

    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleSubmit = async () => {
    if (!container) return;

    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      // Format dates
      const formattedValues = {
        ...values,
        bl_number: values.bl_number || null,
        etd: values.etd ? values.etd.format("YYYY-MM-DD") : null,
        delayed_etd: values.delayed_etd
          ? values.delayed_etd.format("YYYY-MM-DD")
          : null,
        eta: values.eta ? values.eta.format("YYYY-MM-DD") : null,
        eta_customer: values.eta_customer
          ? values.eta_customer.format("YYYY-MM-DD")
          : null,
      };

      // Update container
      const { error } = await supabase
        .from("containers")
        .update(formattedValues)
        .eq("id", container.id);

      if (error) throw error;

      message.success("Container updated successfully");
      setIsModalVisible(false);

      // Refresh container data
      const { data, error: refreshError } = await supabase
        .from("containers")
        .select(
          `
          *,
          transport_type_info:booking_transport_types(*),
          origin_port_info:booking_origin_port(*),
          destination_port_info:booking_destination_port(*)
        `
        )
        .eq("id", containerId)
        .single();

      if (refreshError) throw refreshError;
      setContainer(data);
    } catch (error) {
      console.error("Error saving container:", error);
      message.error("Failed to save container");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Utility functions for vessel tracking
  const isVesselFinderLink = (url: string): boolean => {
    return url?.includes("vesselfinder.com");
  };

  const getVesselFinderId = (url: string): string | undefined => {
    try {
      const urlObj = new URL(url);

      // Check for IMO in query parameters first
      const imoFromQuery = urlObj.searchParams.get("imo");
      if (imoFromQuery) {
        return imoFromQuery;
      }

      // If no IMO in query, check pathname for vessel details
      const matches = urlObj.pathname.match(/\/vessels\/details\/(\d+)/);
      if (matches && matches[1]) {
        return matches[1];
      }
    } catch {
      // If URL parsing fails, try direct regex matches
      const imoMatch = url.match(/[?&]imo=(\d+)/);
      if (imoMatch) {
        return imoMatch[1];
      }

      const vesselMatch = url.match(/vessels\/details\/(\d+)/);
      if (vesselMatch) {
        return vesselMatch[1];
      }
    }
    return undefined;
  };

  const loadLinkableBookings = async (search?: string) => {
    try {
      setIsLoadingAvailableBookings(true);
      const results = await searchBookingsLinkableToContainer(containerId, {
        search,
      });
      setAvailableBookings(results);
    } catch (error) {
      console.error("Error fetching available bookings:", error);
      message.error("Failed to load available bookings");
    } finally {
      setIsLoadingAvailableBookings(false);
    }
  };

  const handleLinkableBookingIdSearch = async (value: string) => {
    const bookingId = parseInt(value.replace(/\D/g, ""), 10);
    if (isNaN(bookingId)) {
      message.warning("Please enter a valid booking ID");
      return;
    }

    try {
      setIsLoadingAvailableBookings(true);
      const result = await lookupBookingForContainerLink(
        containerId,
        bookingId
      );

      if (result.status === "ok") {
        setSelectedBookingId(result.booking.id);
        setAvailableBookings((prev) => {
          if (prev.some((b) => b.id === result.booking.id)) return prev;
          return [result.booking, ...prev];
        });
        message.success(`Found booking #${bookingId}`);
      } else if (result.status === "already_in_container") {
        message.warning(`Booking #${bookingId} is already in this container`);
      } else {
        message.warning(`Booking #${bookingId} not found`);
      }
    } catch (error) {
      console.error("Error looking up booking:", error);
      message.error("Failed to look up booking");
    } finally {
      setIsLoadingAvailableBookings(false);
    }
  };

  // Function to add a booking to this container
  const addBookingToContainer = async () => {
    if (!selectedBookingId) {
      message.warning("Please select a booking to add");
      return;
    }

    try {
      setIsSubmitting(true);

      const { success, error } = await linkBookingToContainer(
        selectedBookingId,
        containerId
      );

      if (!success || error) throw error;

      message.success("Booking added to container successfully");
      setIsAddBookingModalVisible(false);
      setSelectedBookingId(null);

      // Refresh the container bookings data
      setBookingsLoading(true);
      fetchBookings();
    } catch (error) {
      console.error("Error adding booking to container:", error);
      message.error("Failed to add booking to container");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to remove a booking from this container
  const removeBookingFromContainer = async (
    bookingId: number,
    e?: React.MouseEvent
  ) => {
    // Stop propagation to prevent opening the booking
    if (e) e.stopPropagation();

    try {
      // Update the booking's container_id to null
      const { error } = await supabase
        .from("bookings")
        .update({ container_id: null })
        .eq("id", bookingId);

      if (error) throw error;

      message.success("Booking removed from container");

      // Refresh the container bookings data
      setBookingsLoading(true);
      fetchBookings();
    } catch (error) {
      console.error("Error removing booking from container:", error);
      message.error("Failed to remove booking from container");
    }
  };

  // Add a function to navigate to booking details
  const navigateToBookingDetails = (bookingId: number) => {
    // The direct URL approach doesn't work properly
    // Instead, navigate to the BookingsAdmin page and pass the ID as a query parameter
    window.open(
      `/admin/bookings?booking_id=${bookingId}&tab=products`,
      "_blank"
    );
  };

  // Available container statuses
  const containerStatuses = [
    { value: "planned", label: "Planned" },
    { value: "departed", label: "Departed" },
    { value: "arrived", label: "Arrived" },
    { value: "customs", label: "Customs" },
    { value: "completed", label: "Completed" },
  ];

  // Function to open status update modal
  const showStatusUpdateModal = () => {
    if (container) {
      setSelectedStatus(container.status || "");
      setIsStatusModalVisible(true);
    }
  };

  // Function to update container status and all its bookings
  const updateContainerStatus = async () => {
    if (!container || !selectedStatus) return;

    try {
      setIsStatusUpdating(true);

      // 1. Update container status
      const { error: containerError } = await supabase
        .from("containers")
        .update({ status: selectedStatus })
        .eq("id", container.id);

      if (containerError) throw containerError;

      // 2. Map container status to appropriate booking status
      let bookingStatus = "";
      if (selectedStatus === "customs") {
        bookingStatus = "customs_pending_clearance";
      } else if (["planned", "departed", "arrived"].includes(selectedStatus)) {
        bookingStatus = "in_transit";
      } else {
        bookingStatus = selectedStatus; // For completed or other statuses
      }

      // Only update booking statuses if we have a valid mapping
      if (bookingStatus) {
        // Update all bookings in this container to the mapped status
        const { error: bookingsError } = await supabase
          .from("bookings")
          .update({ status: bookingStatus })
          .eq("container_id", container.id);

        if (bookingsError) throw bookingsError;
      }

      // Success
      message.success(`Container status updated to ${selectedStatus}`);
      setIsStatusModalVisible(false);

      // Refresh container data
      const { data, error: refreshError } = await supabase
        .from("containers")
        .select(
          `
          *,
          transport_type_info:booking_transport_types(*),
          origin_port_info:booking_origin_port(*),
          destination_port_info:booking_destination_port(*)
        `
        )
        .eq("id", containerId)
        .single();

      if (refreshError) throw refreshError;
      setContainer(data);

      // Refresh bookings
      fetchBookings();
    } catch (error) {
      console.error("Error updating container status:", error);
      message.error("Failed to update container status");
    } finally {
      setIsStatusUpdating(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!container) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Empty description="Container not found" />
        <Button
          onClick={onBack}
          icon={<ArrowLeftOutlined />}
          style={{ marginTop: 16 }}
        >
          Back to Containers
        </Button>
      </div>
    );
  }

  // Email modal functionality
  const handleShowEmails = async () => {
    if (!container?.reference) {
      message.error("Container reference not found");
      return;
    }

    try {
      setEmailsLoading(true);
      setIsEmailModalVisible(true);

      const emailDataList = await getContainerEmailsDetailed(
        container.reference
      );
      setEmailData(emailDataList);

      if (emailDataList.length === 0) {
        message.info("No email addresses found for this container");
      }
    } catch (error) {
      console.error("Error fetching emails:", error);
      message.error("Failed to fetch email addresses");
    } finally {
      setEmailsLoading(false);
    }
  };

  const handleCopyEmailsFromModal = async () => {
    if (emailData.length === 0) {
      message.warning("No emails to copy");
      return;
    }

    try {
      const emailString = emailData.map((item) => item.email).join(", ");
      await navigator.clipboard.writeText(emailString);

      message.success(
        `Copied ${emailData.length} email address${
          emailData.length > 1 ? "es" : ""
        } to clipboard (comma-separated)`
      );
    } catch (error) {
      console.error("Error copying emails:", error);
      message.error("Failed to copy email addresses");
    }
  };

  const handleCopyEmailsWithSemicolon = async () => {
    if (emailData.length === 0) {
      message.warning("No emails to copy");
      return;
    }

    try {
      const emailString = emailData.map((item) => item.email).join("; ");
      await navigator.clipboard.writeText(emailString);

      message.success(
        `Copied ${emailData.length} email address${
          emailData.length > 1 ? "es" : ""
        } to clipboard (semicolon-separated)`
      );
    } catch (error) {
      console.error("Error copying emails:", error);
      message.error("Failed to copy email addresses");
    }
  };

  // Add the modal JSX
  const addBookingModal = (
    <Modal
      title="Add Booking to Container"
      open={isAddBookingModalVisible}
      onCancel={() => {
        setIsAddBookingModalVisible(false);
        setSelectedBookingId(null);
      }}
      confirmLoading={isSubmitting}
      onOk={addBookingToContainer}
    >
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search by booking ID (e.g. 658 or EFF658)"
          style={{ marginBottom: 16 }}
          loading={isLoadingAvailableBookings}
          onSearch={(value) => {
            if (value) void handleLinkableBookingIdSearch(value);
          }}
          enterButton
        />
        <Select
          style={{ width: "100%" }}
          placeholder="Type to search bookings (shows 50 most recent)"
          loading={isLoadingAvailableBookings}
          onChange={(value) => setSelectedBookingId(value)}
          value={selectedBookingId}
          showSearch
          filterOption={false}
          onSearch={(value) => {
            if (linkableBookingsSearchDebounceRef.current) {
              clearTimeout(linkableBookingsSearchDebounceRef.current);
            }
            linkableBookingsSearchDebounceRef.current = setTimeout(() => {
              void loadLinkableBookings(value);
            }, 300);
          }}
          notFoundContent={
            isLoadingAvailableBookings ? (
              <Spin size="small" />
            ) : (
              "No matching bookings — try searching by ID"
            )
          }
        >
          {availableBookings.map((booking) => {
            const transportType =
              booking.transport_type_info?.abbreviation || "";
            const reference = `EFF${booking.id}${transportType}`;
            const company = booking.company_info?.name || "Unknown";
            return (
              <Option key={booking.id} value={booking.id}>
                {reference} - {company}
              </Option>
            );
          })}
        </Select>
      </div>
    </Modal>
  );

  return (
    <div>
      <Button
        onClick={onBack}
        icon={<ArrowLeftOutlined />}
        style={{ marginBottom: 16 }}
      >
        Back to Containers
      </Button>

      <Card>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <Title level={4} style={{ margin: 0 }}>
                Container Details{" "}
                <Tag color={getContainerStatusColor(container.status || "")}>
                  {container.status
                    ? container.status.charAt(0).toUpperCase() +
                      container.status.slice(1)
                    : "Unknown"}
                </Tag>
              </Title>
              <Space>
                <Tooltip title="View and copy all email addresses associated with this container">
                  <Button
                    icon={<CopyOutlined />}
                    onClick={handleShowEmails}
                    style={{
                      backgroundColor: "#1890ff",
                      borderColor: "#1890ff",
                      color: "white",
                    }}
                    disabled={bookings.length === 0}
                  >
                    View Emails
                  </Button>
                </Tooltip>
                <Button
                  icon={<UnorderedListOutlined />}
                  onClick={() => {
                    window.location.href = `/admin/containers/${containerId}/unload`;
                  }}
                  style={{
                    backgroundColor: "#722ed1",
                    borderColor: "#722ed1",
                    color: "white",
                  }}
                  disabled={bookings.length === 0}
                  title="Open Container Unloading Interface"
                >
                  Unload
                </Button>
                <Tooltip title="This is the only way to update the container status">
                  <Button
                    type="primary"
                    icon={<SwapOutlined />}
                    onClick={showStatusUpdateModal}
                    style={{ background: "#faad14", borderColor: "#faad14" }}
                  >
                    Update Status
                  </Button>
                </Tooltip>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={showEditModal}
                >
                  Edit Container
                </Button>
              </Space>
            </div>
          </Col>

          <Col span={24}>
            <Descriptions
              bordered
              column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
            >
              <Descriptions.Item label="Reference">
                <strong>{container.reference}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="B/L Number">
                <ContainerBlNumberDisplay
                  masterContainerId={masterContainer?.id ?? null}
                  masterReference={masterContainer?.reference ?? null}
                  fallbackBlNumber={container.bl_number}
                  loading={masterContainerLoading}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Transport Type">
                {container.transport_type_info?.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Origin">
                {container.origin_port_info?.name}
                {container.origin_port_info?.country
                  ? ` (${container.origin_port_info.country})`
                  : ""}
              </Descriptions.Item>
              <Descriptions.Item label="Destination">
                {container.destination_port_info?.name}
                {container.destination_port_info?.country
                  ? ` (${container.destination_port_info.country})`
                  : ""}
              </Descriptions.Item>
              <Descriptions.Item label="ETD">
                {container.etd
                  ? dayjs(container.etd).format("DD/MM/YYYY")
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Delayed ETD">
                {container.delayed_etd
                  ? dayjs(container.delayed_etd).format("DD/MM/YYYY")
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="ETA">
                {container.eta
                  ? dayjs(container.eta).format("DD/MM/YYYY")
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Customer ETA">
                {container.eta_customer
                  ? dayjs(container.eta_customer).format("DD/MM/YYYY")
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Days to ETA">
                {container.eta
                  ? (() => {
                      const today = dayjs();
                      const etaDate = dayjs(container.eta);
                      const diff = etaDate.diff(today, "day");

                      let color = "";
                      if (diff < 0) {
                        color = "#ff4d4f"; // Red for past date
                      } else if (diff <= 7) {
                        color = "#faad14"; // Orange/yellow for within a week
                      } else if (diff <= 14) {
                        color = "#52c41a"; // Green for within two weeks
                      }

                      return (
                        <span style={{ color, fontWeight: "bold" }}>
                          {diff < 0
                            ? `${Math.abs(diff)} days ago`
                            : diff === 0
                            ? "Today"
                            : `${diff} days`}
                        </span>
                      );
                    })()
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item
                label="Tracking Number"
                span={container.track_and_trace ? 1 : 1}
              >
                {container.track_and_trace ? (
                  <a
                    href={container.track_and_trace}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Tooltip title="Open tracking link">
                      <LinkOutlined style={{ fontSize: "16px" }} />
                    </Tooltip>
                  </a>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                {dayjs(container.created_at).format("DD/MM/YYYY HH:mm")}
              </Descriptions.Item>
            </Descriptions>
          </Col>

          {/* Container Documents Summary */}
          <Col span={24}>
            <ContainerDocumentsSummary
              containerId={containerId}
              onScrollToDocuments={() => {
                const element = document.getElementById(
                  "container-documents-section"
                );
                if (element) {
                  element.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }
              }}
            />
          </Col>

          {/* Container Tracking Map */}
          {container.track_and_trace && (
            <Col span={24}>
              <Card
                title={
                  <Space>
                    <EnvironmentOutlined />
                    Container Tracking
                  </Space>
                }
                style={{ marginTop: 16 }}
              >
                {isVesselFinderLink(container.track_and_trace) ? (
                  <VesselFinderMap
                    imo={getVesselFinderId(container.track_and_trace)}
                  />
                ) : (
                  <iframe
                    src={container.track_and_trace}
                    style={{ width: "100%", height: "400px", border: "none" }}
                    title="Track and Trace"
                  />
                )}
              </Card>
            </Col>
          )}

          {/* Bill of lading (B/L) — data from master_containers */}
          {masterContainer && (
            <Col span={24}>
              <Card
                title={
                  <Space>
                    <LinkOutlined />
                    Bill of lading (B/L)
                  </Space>
                }
                style={{ marginTop: 16 }}
                loading={masterContainerLoading}
              >
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Descriptions bordered column={2}>
                      <Descriptions.Item label="B/L reference">
                        <Button
                          type="link"
                          onClick={() => {
                            window.location.href = `/admin/containers?tab=master-containers&master_id=${masterContainer.id}&action=manage`;
                          }}
                          style={{ padding: 0, fontWeight: "bold" }}
                        >
                          {masterContainer.reference ||
                            `B/L #${masterContainer.id}`}
                        </Button>
                      </Descriptions.Item>
                      <Descriptions.Item label="Total Containers">
                        {masterContainer.containers?.length || 0}
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>

                  {masterContainer.containers &&
                    masterContainer.containers.length > 0 && (
                      <Col span={24}>
                        <Title level={5}>
                          Other containers under this bill of lading (B/L)
                        </Title>
                        <Space wrap>
                          {masterContainer.containers
                            .filter((c) => c.id !== containerId) // Exclude current container
                            .map((container) => (
                              <Tag
                                key={container.id}
                                color={getContainerStatusColor(
                                  container.status || ""
                                )}
                                style={{ cursor: "pointer" }}
                                onClick={() => {
                                  window.open(
                                    `/admin/containers/${container.id}`,
                                    "_blank"
                                  );
                                }}
                              >
                                {container.reference ||
                                  `Container ${container.id}`}
                              </Tag>
                            ))}
                        </Space>
                        {masterContainer.containers.filter(
                          (c) => c.id !== containerId
                        ).length === 0 && (
                          <Text type="secondary">
                            This is the only container under this bill of lading
                            (B/L).
                          </Text>
                        )}
                      </Col>
                    )}
                </Row>
              </Card>
            </Col>
          )}

          <Col span={24}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 24,
                marginBottom: 16,
              }}
            >
              <Title level={4} style={{ margin: 0 }}>
                Bookings in this Container
                <Badge
                  count={bookings.length}
                  style={{ marginLeft: 8, backgroundColor: "#52c41a" }}
                  showZero
                />
              </Title>
              <Space>
                <Button
                  type="primary"
                  onClick={() => {
                    setSelectedBookingId(null);
                    setAvailableBookings([]);
                    setIsAddBookingModalVisible(true);
                    void loadLinkableBookings();
                  }}
                >
                  Add Booking
                </Button>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={handlePrintBookings}
                  disabled={bookings.length === 0}
                >
                  Print Bookings
                </Button>
              </Space>
            </div>

            <Table
              columns={bookingsColumns}
              dataSource={bookings}
              rowKey="id"
              loading={bookingsLoading}
              pagination={false}
              locale={{ emptyText: "No bookings in this container" }}
              size="small"
              onRow={(record) => ({
                onClick: () => navigateToBookingDetails(record.id),
                style: { cursor: "pointer" },
              })}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={6}>
                      <strong>Totals</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6}>
                      <strong>{bookingsTotalCartons}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7}>
                      <strong>{bookingsTotalWeight.toFixed(2)} kg</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={8}>
                      <strong>{bookingsTotalCbm} m³</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={9}>
                      {/* Delivery column - empty */}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={10}>
                      {/* Export license - empty */}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={11}>
                      {/* Commodity inspection - empty */}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={12}>
                      {/* Post export license - empty */}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={13}>
                      {/* Pickup fee - empty */}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={14}>
                      {/* Actions column - empty */}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </Col>

          <Col span={24}>
            <Divider />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <Title level={4} style={{ margin: 0 }}>
                <GiftOutlined style={{ marginRight: 8 }} />
                All Products in Container
                <Badge
                  count={filteredProducts.length}
                  style={{ marginLeft: 8, backgroundColor: "#1890ff" }}
                  showZero
                />
              </Title>
              <Space>
                <Checkbox
                  checked={filterOutSingleProducts}
                  onChange={(e) => setFilterOutSingleProducts(e.target.checked)}
                >
                  Filter out single products
                </Checkbox>
                <Select
                  value={printFilter}
                  onChange={setPrintFilter}
                  style={{ width: 140 }}
                  size="small"
                >
                  <Option value="all">All</Option>
                  <Option value="storage">In storage</Option>
                  <Option value="delivery">Delivery</Option>
                </Select>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={handlePrintProducts}
                  disabled={products.length === 0}
                >
                  Print Products
                </Button>
              </Space>
            </div>

            <Table
              columns={productsColumns}
              dataSource={filteredProducts}
              rowKey="id"
              loading={productsLoading}
              pagination={false}
              locale={{
                emptyText: printFilter === "all"
                  ? "No products in this container"
                  : `No ${printFilter === "storage" ? "products in storage" : "products for delivery"}`
              }}
              size="small"
              scroll={{ x: 1200 }}
              onRow={(record) => ({
                onClick: () => navigateToBookingDetails(record.booking_id),
                style: { cursor: "pointer" },
              })}
              summary={() => {
                // Calculate totals for filtered products
                const filteredTotalPieces = filteredProducts.reduce(
                  (sum, product) => sum + (product.pieces || 0),
                  0
                );
                const filteredTotalCartons = filteredProducts.reduce(
                  (sum, product) => sum + (product.cartons || 0),
                  0
                );
                const filteredTotalWeight = filteredProducts.reduce(
                  (sum, product) => sum + (product.gross_weight || 0),
                  0
                );
                const filteredTotalCbm = filteredProducts
                  .reduce((sum, product) => sum + (product.cbm || 0), 0)
                  .toFixed(3);

                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={5}>
                        <strong>Totals</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5}>
                        <strong>{filteredTotalPieces}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6}>
                        <strong>{filteredTotalCartons}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7}>
                        <strong>{filteredTotalWeight} kg</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={8}>
                        <strong>{filteredTotalCbm} m³</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={9}>
                        {/* Delivery column - empty */}
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Col>

          {/* Container Documents Section */}
          <Col span={24}>
            <ContainerMasterAwbBlReadOnly
              containerId={containerId}
              refreshToken={masterContainer?.id ?? null}
              style={{ marginTop: 24 }}
            />
            <ContainerDocuments containerId={containerId} />
          </Col>
        </Row>
      </Card>

      <Modal
        title="Edit Container"
        open={isModalVisible}
        onCancel={handleCancel}
        onOk={handleSubmit}
        confirmLoading={isSubmitting}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="reference"
                label="Container Reference"
                rules={[{ required: true, message: "Please enter reference" }]}
              >
                <Input placeholder="Enter container reference" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="transport_type"
                label="Transport Type"
                rules={[
                  { required: true, message: "Please select transport type" },
                ]}
              >
                <Select placeholder="Select transport type">
                  {transportTypes.map((type) => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="bl_number" label="B/L Number">
                <Input placeholder="Enter bill of lading number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="track_and_trace" label="Tracking Number">
                <Input placeholder="Enter tracking number" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="origin_port"
                label="Origin Port"
                rules={[
                  { required: true, message: "Please select origin port" },
                ]}
              >
                <Select
                  placeholder="Select origin port"
                  showSearch
                  optionFilterProp="children"
                >
                  {originPorts.map((port) => (
                    <Option key={port.id} value={port.id}>
                      {port.name} ({port.country})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="destination_port"
                label="Destination Port"
                rules={[
                  { required: true, message: "Please select destination port" },
                ]}
              >
                <Select
                  placeholder="Select destination port"
                  showSearch
                  optionFilterProp="children"
                >
                  {destinationPorts.map((port) => (
                    <Option key={port.id} value={port.id}>
                      {port.name} ({port.country})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="etd"
                label="ETD (original)"
                tooltip="Original booked departure date — used for invoicing"
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="delayed_etd"
                label="Delayed ETD"
                tooltip="Optional operational delayed departure. When set, this is shown instead of the original ETD."
              >
                <DatePicker style={{ width: "100%" }} allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="eta" label="ETA (Estimated Time of Arrival)">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="eta_customer"
                label="Customer ETA"
                tooltip="Expected arrival at customer after port arrival and customs clearance"
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Add the modal */}
      {addBookingModal}

      {/* Email modal */}
      <Modal
        title={`Email Addresses for Container ${container?.reference || ""}`}
        open={isEmailModalVisible}
        onCancel={() => setIsEmailModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsEmailModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="copy-comma"
            icon={<CopyOutlined />}
            onClick={handleCopyEmailsFromModal}
            disabled={emailData.length === 0}
          >
            Copy (Comma)
          </Button>,
          <Button
            key="copy-semicolon"
            type="primary"
            icon={<CopyOutlined />}
            onClick={handleCopyEmailsWithSemicolon}
            disabled={emailData.length === 0}
          >
            Copy (Semicolon)
          </Button>,
        ]}
        width={800}
      >
        {emailsLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
            <p style={{ marginTop: 16 }}>Fetching email addresses...</p>
          </div>
        ) : emailData.length === 0 ? (
          <Empty
            description="No email addresses found for this container"
            style={{ padding: "40px" }}
          />
        ) : (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>
                Found {emailData.length} email address
                {emailData.length > 1 ? "es" : ""} from{" "}
                {new Set(emailData.map((item) => item.companyName)).size} compan
                {new Set(emailData.map((item) => item.companyName)).size > 1
                  ? "ies"
                  : "y"}
                :
              </Text>
            </div>
            <Table
              dataSource={emailData}
              rowKey={(record) =>
                `${record.companyName}-${record.email}-${record.bookingId}`
              }
              pagination={false}
              size="small"
              columns={[
                {
                  title: "Company",
                  dataIndex: "companyName",
                  key: "companyName",
                  width: "40%",
                },
                {
                  title: "Email Address",
                  dataIndex: "email",
                  key: "email",
                  width: "50%",
                  render: (email: string) => (
                    <Text copyable style={{ fontFamily: "monospace" }}>
                      {email}
                    </Text>
                  ),
                },
                {
                  title: "Booking ID",
                  dataIndex: "bookingId",
                  key: "bookingId",
                  width: "10%",
                  render: (id: number) => (
                    <Tag
                      color="blue"
                      style={{ cursor: "pointer" }}
                      onClick={() => navigateToBookingDetails(id)}
                    >
                      {id}
                    </Tag>
                  ),
                },
              ]}
            />
            <div
              style={{
                marginTop: 16,
                padding: "12px",
                backgroundColor: "#f5f5f5",
                borderRadius: "6px",
              }}
            >
              <Text strong>Comma-separated (Gmail, etc.):</Text>
              <div
                style={{
                  marginTop: 8,
                  fontFamily: "monospace",
                  fontSize: "12px",
                  wordBreak: "break-all",
                }}
              >
                {emailData.map((item) => item.email).join(", ")}
              </div>
            </div>
            <div
              style={{
                marginTop: 12,
                padding: "12px",
                backgroundColor: "#e6f7ff",
                borderRadius: "6px",
              }}
            >
              <Text strong>Semicolon-separated (Outlook, etc.):</Text>
              <div
                style={{
                  marginTop: 8,
                  fontFamily: "monospace",
                  fontSize: "12px",
                  wordBreak: "break-all",
                }}
              >
                {emailData.map((item) => item.email).join("; ")}
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Status update modal */}
      <Modal
        title="Update Container Status"
        open={isStatusModalVisible}
        onCancel={() => setIsStatusModalVisible(false)}
        onOk={updateContainerStatus}
        confirmLoading={isStatusUpdating}
        width={700}
      >
        <Alert
          message="Warning"
          description="Updating the container status will also update the status of all bookings in this container."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form layout="vertical">
          <Form.Item label="Container Status">
            <Select
              value={selectedStatus}
              onChange={setSelectedStatus}
              style={{ width: "100%" }}
            >
              {containerStatuses.map((status) => (
                <Option key={status.value} value={status.value}>
                  {status.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedStatus && bookings.length > 0 && (
            <>
              <Divider />
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Booking Status Changes</Title>
                <Text>The following bookings will be updated:</Text>
              </div>
              <Table
                size="small"
                pagination={false}
                dataSource={bookings}
                rowKey="id"
                columns={[
                  {
                    title: "Booking",
                    dataIndex: "id",
                    key: "id",
                    render: (id, record) => (
                      <Tag color="blue">
                        EFF{id}
                        {record.transport_type_info?.abbreviation || ""}
                      </Tag>
                    ),
                  },
                  {
                    title: "Company",
                    dataIndex: ["company_info", "name"],
                    key: "company",
                    render: (text) => text || "-",
                  },
                  {
                    title: "Current Status",
                    dataIndex: "status",
                    key: "currentStatus",
                    render: (status) => {
                      const normalizedStatus = normalizeBookingStatus(status);
                      const statusColor =
                        statusColors[
                          normalizedStatus as keyof typeof statusColors
                        ] || "default";
                      return <Tag color={statusColor}>{normalizedStatus}</Tag>;
                    },
                  },
                  {
                    title: "New Status",
                    key: "newStatus",
                    render: () => {
                      let newStatus = "";
                      if (selectedStatus === "customs") {
                        newStatus = "customs_pending_clearance";
                      } else if (
                        ["planned", "departed", "arrived"].includes(
                          selectedStatus
                        )
                      ) {
                        newStatus = "in_transit";
                      } else {
                        newStatus = selectedStatus;
                      }
                      const normalizedStatus =
                        normalizeBookingStatus(newStatus);
                      const statusColor =
                        statusColors[
                          normalizedStatus as keyof typeof statusColors
                        ] || "default";
                      return <Tag color={statusColor}>{normalizedStatus}</Tag>;
                    },
                  },
                ]}
              />
            </>
          )}

          <div style={{ marginTop: 16 }}>
            <Text strong>Affected bookings: </Text>
            <Text>{bookings.length}</Text>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ContainerDetails;
